"use client";

import { Fragment } from "react";
import { useTranslations } from "next-intl";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { Field } from "../../forms/field";
import Button from "../../shared/ui/button";
import { useForm } from "react-hook-form";
import Icon from "../../shared/ui/icon";
import { REQUIRED } from "../../../constants/regex-const";

type SaveFavoriteForm = {
  name: string;
};

type SaveFavoriteDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string) => void;
  isSaving?: boolean;
  adresse?: {
    rue: string;
    numero: string;
    boite: string;
    codePostal: string;
    ville: string;
    pays: string;
  };
};

const SaveFavoriteDrawer = ({
  isOpen,
  onClose,
  onSave,
  isSaving,
  adresse,
}: SaveFavoriteDrawerProps) => {
  const t = useTranslations("save_favorite_drawer");
  const tCommon = useTranslations("common");
  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<SaveFavoriteForm>({
    defaultValues: { name: "" },
  });

  const onSubmit = (data: SaveFavoriteForm) => {
    onSave(data.name);
  };

  const handleClose = () => {
    if (isSaving) return;
    reset();
    onClose();
  };

  const hasAddress = adresse && adresse.rue;

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={handleClose} className="dark relative z-1000">
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </TransitionChild>

        <div className="fixed inset-0 flex justify-end">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="ease-in duration-200"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <DialogPanel className="w-full max-w-md bg-white dark:bg-slate-900 shadow-xl flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700/50">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-950/30 text-amber-500">
                    <Icon name="star" className="text-xl" />
                  </div>
                  <DialogTitle className="text-lg font-bold text-slate-800 dark:text-white">
                    {t("title")}
                  </DialogTitle>
                </div>
                <button
                  onClick={handleClose}
                  disabled={isSaving}
                  className="flex items-center justify-center w-9 h-9 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 dark:hover:text-slate-300 transition-all disabled:opacity-50"
                >
                  <Icon name="close" className="text-lg" />
                </button>
              </div>

              {/* Content */}
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex-1 overflow-y-auto p-6 space-y-6"
              >
                <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                  {t("description")}
                </p>

                {/* Aperçu adresse */}
                {hasAddress && (
                  <div className="rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/50 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon
                        name="location_on"
                        className="text-base text-slate-400"
                      />
                      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                        {t("address_label")}
                      </p>
                    </div>
                    <p className="text-sm font-semibold text-slate-800 dark:text-white">
                      {adresse.rue} {adresse.numero}
                      {adresse.boite ? ` / ${adresse.boite}` : ""}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {adresse.codePostal} {adresse.ville}, {adresse.pays}
                    </p>
                  </div>
                )}

                {!hasAddress && (
                  <div className="rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-500/30 p-4 flex items-center gap-3">
                    <Icon name="warning" className="text-amber-500" />
                    <p className="text-sm text-amber-700 dark:text-amber-400">
                      {t("address_required")}
                    </p>
                  </div>
                )}

                <Field
                  titre={t("name_label")}
                  placeholder={t("name_placeholder")}
                  error={errors.name?.message}
                  {...register("name", REQUIRED(tCommon))}
                />
              </form>

              {/* Footer */}
              <div className="flex items-center gap-3 p-6 border-t border-slate-200 dark:border-slate-700/50">
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={handleClose}
                  disabled={isSaving}
                >
                  {t("cancel")}
                </Button>
                <Button
                  icon={isSaving ? undefined : "star"}
                  variant="primary"
                  className="flex-1"
                  onClick={handleSubmit(onSubmit)}
                  disabled={isSaving || !hasAddress}
                >
                  {isSaving ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t("saving")}
                    </div>
                  ) : (
                    t("save")
                  )}
                </Button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
};

export default SaveFavoriteDrawer;
